import { ChangeDetectionStrategy, ChangeDetectorRef, Component, InjectionToken, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EMPTY, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { filter, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { catchError, map } from 'rxjs/operators';
import { IQuestion } from '../../../../interfaces/question.interface';
import { CheckListsService } from './check-lists.service';
import { IChecklist } from '../../../../interfaces/checklist.interface';
import { ICheckListsFilters } from '../check-lists-filters/check-lists-filters.component';
import { listAnimation } from '../../animations/list.animation';
import { SortDirectionEnum } from '../../../../enums/sort-direction.enum';
import { ISaveChecklist } from '../../../../interfaces/save-checklist.interface';
import { IDeleteQuestionError } from '../../../../interfaces/delete-questions-response.interface';
import { TicketTypeEnum } from '../../../../enums/ticket-type.enum';
import { CheckListsValidators } from '../../../../check-lists.validators';
import { QuestionTypeEnum } from '../../../../enums/question-type.enum';

export const QUESTION_DIALOG_PARAMS = new InjectionToken('question-categories');

export interface IChecklistFormValue {
  hdTicketTypes: number[];
  checklist: IChecklist;
  hdChecklist: IChecklist;
}

export interface CreateChecklistParams {
  name: string;
  hdTicketTypes: number[];
  gpSubtypes?: number[];
}

@Component({
  templateUrl: './check-lists.component.html',
  styleUrls: ['./check-lists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CheckListsService],
  animations: [listAnimation],
  standalone: false,
})
export class CheckListsComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject();
  private MAX_SELECTION = 10;
  private checklist: IChecklist;

  hasChanges = false;
  hasEnablerChanges = false;

  questions$ = this.checkListsService.questions$;
  checklists$ = this.checkListsService.checklists$;
  checklist$ = this.checkListsService.checklist$;
  categories$ = this.checkListsService.categories$;
  hdTicketTypes$ = this.checkListsService.hdTicketTypes$;

  selectedPage = 1;

  paramsCategory: ICheckListsFilters = {
    category: '',
    search: '',
  };

  paginationConfig = {
    collectionSize: 0,
    page: this.selectedPage,
    pageSize: 100,
    maxSize: 5,
    rotate: false,
  };

  questionArray = this.fb.array([]);
  checklistQuestionArray = this.fb.array([]);
  selectAllQuestions = this.fb.control(false);
  selectAllChecklist = this.fb.control(false);
  checklistForm = this.fb.group(
    {
      checklist: null,
      hdChecklist: null,
      hdTicketTypes: [],
      gpSubtypes: [],
      gpChecklist: null,
    },
    {
      validators: CheckListsValidators.requiredIfLocal,
    }
  );
  checklistEnabler = this.fb.control(false);

  constructor(
    private fb: UntypedFormBuilder,
    private checkListsService: CheckListsService,
    private cdr: ChangeDetectorRef
  ) {}

  get selectedChecklist(): IChecklist {
    const { checklist, hdChecklist, gpChecklist } = this.checklistForm.value;

    return checklist && checklist.ticket_type === TicketTypeEnum.Local
      ? hdChecklist
      : checklist && checklist.ticket_type === TicketTypeEnum.GlobalProblem
        ? gpChecklist
        : checklist;
  }

  get selectedHDChecklist(): IChecklist {
    return this.checklistForm.get('hdChecklist').value;
  }

  get isLocalChecklist(): boolean {
    const { checklist } = this.checklistForm.value;
    return checklist && checklist.ticket_type === TicketTypeEnum.Local;
  }

  get isProbleChecklist(): boolean {
    const { checklist } = this.checklistForm.value;
    return checklist && checklist.ticket_type === TicketTypeEnum.GlobalProblem;
  }

  get checklistQuestionGroups(): UntypedFormGroup[] {
    return this.checklistQuestionArray.controls as UntypedFormGroup[];
  }

  get questionGroups(): UntypedFormGroup[] {
    return this.questionArray.controls as UntypedFormGroup[];
  }

  ngOnInit(): void {
    this.checkListsService.loadQuestions();
    this.checkListsService.loadChecklists();
    this.checkListsService.loadQuestionCategories();
    this.checkListsService.loadHDTicketTypes();
    this.checkListsService.loadGPSubtypes();

    this.questionArray.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((values) => {
      const foundSelected = Boolean(values.find((value) => value.selected));
      this.selectAllQuestions.setValue(foundSelected);
    });

    this.checklistQuestionArray.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((values) => {
      const foundSelected = Boolean(values.find((value) => value.selected));
      this.selectAllChecklist.setValue(foundSelected);
    });

    this.questions$
      .pipe(
        map((qs: IQuestion[]) => this.mapToGroups(qs)),
        takeUntil(this.destroyed$)
      )
      .subscribe((groups: UntypedFormGroup[]) => {
        this.paginationConfig.collectionSize = this.checkListsService.totalItem;
        this.questionArray.clear();
        groups.forEach((g) => this.questionArray.push(g));
      });

    this.checklist$
      .pipe(
        tap((ct: IChecklist) => {
          this.checklist = ct;
          this.checklistEnabler.setValue(ct.is_enabled, {
            emitEvent: false,
          });
        }),
        map((ct: IChecklist) => this.mapToGroups(ct.questions)),
        takeUntil(this.destroyed$)
      )
      .subscribe((groups: UntypedFormGroup[]) => {
        this.checklistQuestionArray.clear();
        this.hasChanges = false;
        groups.forEach((g: UntypedFormGroup) => this.checklistQuestionArray.push(g));
      });
  }

  filterRoot(checklists?: IChecklist[]): IChecklist[] {
    if (!checklists) {
      return [];
    }

    return checklists.filter((checklist) => !checklist.parent);
  }

  isIncluded(group: UntypedFormGroup): boolean {
    return this.checklistQuestionArray.controls.findIndex((g) => g.value.question.id === group.value.question.id) >= 0;
  }

  handlePageChange(page: number): void {
    this.paginationConfig.page = page;
    this.loadQuestionsWithPagination();
  }

  loadQuestionsWithPagination(): void {
    const params = {
      category: this.paramsCategory.category,
      search: this.paramsCategory.search,
      limit: this.paginationConfig.pageSize,
      offset: (this.paginationConfig.page - 1) * this.paginationConfig.pageSize,
    };
    this.checkListsService.loadQuestions(params);
  }

  handleMove(question: IQuestion): void {
    if (this.handleQualityKeeper(question)) {
      return;
    }

    const [group] = this.mapToGroups([question]);
    this.checklistQuestionArray.push(group);
    this.hasChanges = true;
    this.cdr.markForCheck();
  }

  handleChangeEnabler(): void {
    this.hasEnablerChanges = true;
  }

  handleDeleteQuestions(group: UntypedFormGroup[], params: ICheckListsFilters) {
    const selectedGroups = group.filter((g) => g.value.selected);

    if (!selectedGroups.length) {
      return;
    }

    this.checkListsService
      .showDeleteDialog(`Единовременно можно удалить 10 вопросов. Будут удалены первые ${selectedGroups.length} выделенных. Вы согласны?`)
      .pipe(filter(Boolean))
      .subscribe(() => this.tryToDeleteQuestions(selectedGroups, params));
  }

  handleDeleteChecklist(groups: UntypedFormGroup[]): void {
    const selectedGroups = groups.filter((g) => g.value.selected);

    if (!selectedGroups.length) {
      return;
    }

    const afterPortHandling$ = this.handleDeletePort(selectedGroups);

    afterPortHandling$
      .pipe(
        switchMap((filteredGroups) => {
          return this.checkListsService
            .showDeleteDialog(
              groups.length === filteredGroups.length
                ? 'Будут удалены все подобранные вопросы. Вы согласны?'
                : `Будут удалены ${filteredGroups.length} подобранных вопросов. Вы согласны?`
            )
            .pipe(
              tap((result) => {
                if (result) {
                  return;
                }
                filteredGroups.forEach((g) => g.patchValue({ selected: false }));
              }),
              filter(Boolean),
              mapTo(filteredGroups)
            );
        })
      )
      .subscribe((filteredGroups: UntypedFormGroup[]) => {
        filteredGroups.forEach((filteredGroup) => {
          const foundIndex = this.checklistQuestionArray.controls.findIndex((g) => g === filteredGroup);
          this.checklistQuestionArray.removeAt(foundIndex);
        });
        this.selectAllChecklist.setValue(false);
        this.hasChanges = true;
        this.cdr.markForCheck();
      });
  }

  handleSort(direction: SortDirectionEnum, group: UntypedFormGroup) {
    const foundIndex = this.checklistQuestionArray.controls.findIndex((g) => g === group);

    if ((foundIndex === 0 && direction === SortDirectionEnum.Up) || (foundIndex === this.checklistQuestionArray.length - 1 && direction === SortDirectionEnum.Down)) {
      return;
    }

    this.checklistQuestionArray.removeAt(foundIndex);

    if (direction === SortDirectionEnum.Down) {
      this.checklistQuestionArray.insert(foundIndex + 1, group);
    } else {
      this.checklistQuestionArray.insert(foundIndex - 1, group);
    }

    this.hasChanges = true;
    this.cdr.markForCheck();
  }

  handleRequired(isRequired: boolean, group: UntypedFormGroup) {
    group.patchValue({
      question: {
        ...group.value.question,
        is_required: isRequired,
      },
    });
    this.hasChanges = true;
  }

  handleSelectAll(selectAll: boolean, groups: UntypedFormGroup[], max = this.MAX_SELECTION): void {
    const changedGroups = !selectAll ? groups : groups.slice(0, max);
    changedGroups.forEach((group) => group.patchValue({ selected: selectAll }));
  }

  handleChangeCategory(params): void {
    this.paramsCategory = params;
    this.selectAllQuestions.setValue(false);
    this.checkListsService.loadQuestions(params);
    this.loadQuestionsWithPagination();
  }

  handleApply(params): void {
    this.paramsCategory = params;
    this.checkListsService.loadQuestions(params);
    this.loadQuestionsWithPagination();
  }

  handleReset(params): void {
    this.paramsCategory = params;
    this.checkListsService.loadQuestions(params);
    this.loadQuestionsWithPagination();
  }

  handleCreate(params: ICheckListsFilters): void {
    this.paramsCategory = params;
    this.checkListsService.showQuestionDialog().subscribe((data) => {
      this.createQuestion(data, params);
    });
  }

  handleChangeTicketType() {
    this.hasChanges = true;
  }

  handleChangeChecklist(selectedChecklist: IChecklist): void {
    if (!selectedChecklist) {
      return;
    }

    if (!selectedChecklist.parent) {
      this.checklistForm.patchValue({ hdChecklist: null });
    }

    this.checklistEnabler.enable();
    this.checklistForm.patchValue({ selectAll: false });
    this.checklistQuestionArray.clear();

    if (selectedChecklist.ticket_type === TicketTypeEnum.Local) {
      this.checklistEnabler.disable();
      return;
    }

    this.checkListsService.loadChecklist(selectedChecklist.ticket_type);
  }

  handleChecklistCancel(): void {
    this.checklistForm.reset({
      ticketType: null,
    });
    this.checklistQuestionArray.clear();
  }

  saveChecklistState(): Observable<IChecklist> {
    const { hdTicketTypes, gpSubtypes } = this.checklistForm.value;
    const { ticket_type, name, hd_ticket_types, gp_subtypes } = this.selectedChecklist;
    const is_enabled = this.checklistEnabler.value;

    const params: any = {
      ticket_type,
      name,
      is_enabled,
    };
    if (this.isLocalChecklist) {
      params.hd_ticket_types = hdTicketTypes || hd_ticket_types;
    } else {
      params.gp_subtypes = gpSubtypes || gp_subtypes;
    }

    return this.checkListsService.saveChecklist(this.selectedChecklist.ticket_type, params).pipe(
      catchError((e) => {
        this.showSaveChecklistErrors(e);
        return throwError(e);
      })
    );
  }

  handleCreateChecklist({ name, hdTicketTypes, gpSubtypes }: CreateChecklistParams): void {
    const {
      checklist: { id: parent },
    } = this.checklistForm.value as IChecklistFormValue;

    const is_enabled = this.checklistEnabler.value;
    const { checklist } = this.checklistForm.value as IChecklistFormValue;

    const params: any = {
      parent,
      name,
      ticket_type: Date.now().toString(),
      is_enabled,
    };
    if (checklist.ticket_type === TicketTypeEnum.Local) {
      params.hd_ticket_types = hdTicketTypes;
    } else if (checklist.ticket_type === TicketTypeEnum.GlobalProblem) {
      params.gp_subtypes = gpSubtypes;
    }

    this.checkListsService
      .createChecklist(params)
      .pipe(
        catchError((e) => {
          this.showSaveChecklistErrors(e);
          return throwError(e);
        })
      )
      .subscribe(() => {
        this.checkListsService.showSuccessDialog(`Чек-лист "${name}" успешно создан.`);

        this.checkListsService.loadChecklists();
      });
  }

  handleSaveChecklist(): void {
    this.checkListsService
      .showSaveDialog(this.selectedChecklist.name, this.hasChanges, this.checklistEnabler.value !== this.checklist.is_enabled)
      .pipe(
        switchMap(() => {
          return forkJoin([this.hasChanges ? this.saveChecklistQuestions() : of(null), this.saveChecklistState()]);
        }),
        switchMap(() => {
          return this.checkListsService.showSavedDialog(this.selectedChecklist.name);
        })
      )
      .subscribe(() => {
        this.hasChanges = false;
        this.hasEnablerChanges = false;

        // Определяем, какой ID использовать для загрузки чек-листа
        let loadTicketType = this.selectedChecklist.ticket_type;
        if (this.isProbleChecklist) {
          const { gpSubtypes } = this.checklistForm.value;
          if (gpSubtypes && gpSubtypes.length) {
            // Ищем чеклист для подтипа в списке всех чеклистов
            const subtypeId = gpSubtypes[0];
            const subtypeChecklist = this.filterProblem(this.checklists$.getValue()).find((checklist) => checklist.gp_subtypes && checklist.gp_subtypes.includes(subtypeId));

            if (subtypeChecklist) {
              // Используем ticket_type из найденного чеклиста подтипа
              loadTicketType = subtypeChecklist.ticket_type;
            }
          }
        }

        this.checkListsService.loadChecklist(loadTicketType);
        this.cdr.markForCheck();
      });
  }

  private showSaveChecklistErrors(err: any): void {
    let messageToDisplay = 'Произошла ошибка при сохранении чек-листа. Пожалуйста, проверьте введенные данные и попробуйте снова.'; // Default generic message

    if (err && err.error && err.error.data) {
      const data = err.error.data;
      const errorMessages: string[] = [];

      // Check for hd_ticket_types errors
      if (data.hd_ticket_types && Array.isArray(data.hd_ticket_types)) {
        data.hd_ticket_types.forEach((msg: any) => {
          if (typeof msg === 'string') errorMessages.push(msg);
        });
      }

      // Check for gp_subtypes errors (relevant for Global Problems)
      if (data.gp_subtypes && Array.isArray(data.gp_subtypes)) {
        data.gp_subtypes.forEach((msg: any) => {
          if (typeof msg === 'string') errorMessages.push(msg);
        });
      }

      // If no specific field errors, check for a general 'message' in data
      if (errorMessages.length === 0 && data.message && typeof data.message === 'string') {
        errorMessages.push(data.message);
      }

      // Check for other common error structures like 'errors' array or object
      // (e.g. if the backend returns errors in a generic 'errors' field)
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          data.errors.forEach((errMsg: any) => {
            if (typeof errMsg === 'string') errorMessages.push(errMsg);
          });
        } else if (typeof data.errors === 'object' && data.errors !== null) {
          // Handle cases where errors is an object with field names as keys
          // and error messages (or arrays of messages) as values.
          Object.values(data.errors).forEach((fieldErrors: any) => {
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach((errMsg: any) => {
                if (typeof errMsg === 'string') errorMessages.push(errMsg);
              });
            } else if (typeof fieldErrors === 'string') {
              errorMessages.push(fieldErrors);
            }
          });
        }
      }

      // If any messages were collected, join them. Otherwise, the default message will be used.
      if (errorMessages.length > 0) {
        messageToDisplay = errorMessages.join('<br />');
      }
    } else if (err && err.message && typeof err.message === 'string') {
      // Fallback to a top-level error message if the error structure is simpler
      // (e.g., network error or a non-standard API error response)
      messageToDisplay = err.message;
    }

    this.checkListsService.showErrorDialog(messageToDisplay);
  }

  private handleDeletePort(selectedGroups: UntypedFormGroup[]): Observable<UntypedFormGroup[]> {
    const foundPortQuestion = selectedGroups.find((group) => group.value.question.type === QuestionTypeEnum.Port);
    const qualityKeeperQuestions = this.checklistQuestionGroups.filter((group) => group.value.question.type === QuestionTypeEnum.QualityKeeper);
    const qualityKeeperAndPortQuestions = this.checklistQuestionGroups.filter(
      (g) => g.value.question.type === QuestionTypeEnum.QualityKeeper || g.value.question.type === QuestionTypeEnum.Port
    );

    if (!foundPortQuestion || !qualityKeeperQuestions.length) {
      return of(selectedGroups);
    }

    return this.checkListsService
      .showDeleteDialog(
        `
      Уведомление "Вопрос [+IP&ports+] необходим для проверки в системе Quality Keeper. Вместе с вопросом [+IP&ports+]
      будет удален вопрос [+quality_keeper+]".
    `,
        'Согласен'
      )
      .pipe(
        switchMap((result: boolean) => {
          const otherSelectedGroups = selectedGroups.filter(
            (g: UntypedFormGroup) =>
              g.value.question.type !== QuestionTypeEnum.Port && g.value.question.type !== QuestionTypeEnum.QualityKeeper
          );

          if (!result && !otherSelectedGroups.length) {
            selectedGroups.forEach((g) => g.patchValue({ selected: false }));
            return EMPTY;
          }

          if (result) {
            qualityKeeperAndPortQuestions.forEach((g) => g.patchValue({ selected: true }));
          }

          const filteredGroups = result ? qualityKeeperAndPortQuestions.concat(otherSelectedGroups) : otherSelectedGroups;

          return of(filteredGroups);
        })
      );
  }

  private handleQualityKeeper(question: IQuestion): boolean {
    if (question.type !== QuestionTypeEnum.QualityKeeper) {
      return false;
    }

    const foundQuestion = this.checklistQuestionArray.controls.find((questionGroup: UntypedFormGroup) => questionGroup.value.question.type === QuestionTypeEnum.Port);

    if (foundQuestion) {
      return false;
    }

    this.checkListsService.showErrorDialog(`
      Проверка в системе Quality Keeper возможна, только совместно с вопросом [+IP&ports+].
      Сначала необходимо в чек-лист добавить вопрос [+IP&ports+]
    `);

    return true;
  }

  public filterLocal(checklists: IChecklist[]): IChecklist[] {
    const localChecklist = checklists.find((checklist: IChecklist) => checklist.ticket_type === TicketTypeEnum.Local);

    if (!localChecklist) {
      return [];
    }

    return checklists.filter((checklist: IChecklist) => checklist.parent === localChecklist.id);
  }

  public filterProblem(checklists: IChecklist[]): IChecklist[] {
    const globalChecklist = checklists.find((checklist: IChecklist) => checklist.ticket_type === TicketTypeEnum.GlobalProblem);

    if (!globalChecklist) {
      return [];
    }

    return checklists.filter((checklist: IChecklist) => checklist.parent === globalChecklist.id);
  }

  private createQuestion(
    {
      createCategory,
      ...question
    }: Partial<IQuestion> & {
      createCategory: string;
    },
    params: ICheckListsFilters
  ): void {
    const createdCategory$ = createCategory ? this.checkListsService.createCategory(createCategory) : of(null);

    createdCategory$
      .pipe(
        switchMap((createdCategory) => {
          const formattedQuestion = createdCategory ? { ...question, category: createdCategory.id } : question;
          return this.checkListsService.createQuestion(formattedQuestion);
        }),
        catchError((error) => {
          return this.checkListsService.showErrorDialog('При создании вопроса что-то пошло не так').pipe(mapTo(throwError(error)));
        }),
        switchMap(() => this.checkListsService.showSuccessDialog('Вопрос успешно создан!'))
      )
      .subscribe(() => {
        this.checkListsService.loadQuestionCategories();
        this.handleApply(params);
      });
  }

  private saveChecklistQuestions(): Observable<IChecklist> {
    // Для GlobalProblem нам нужен выбранный подтип
    let selectedGPSubtypes = null;
    if (this.isProbleChecklist) {
      const { gpSubtypes } = this.checklistForm.value;
      selectedGPSubtypes = gpSubtypes;
    }

    const saveChecklist = this.checklistQuestionArray.controls.map((group) => {
      const {
        question: { id, is_required },
      } = group.value as { question: IQuestion };

      const result: ISaveChecklist = {
        question: id,
        is_required: Boolean(is_required),
      };

      // Если выбран тип global_problem и подтип, добавляем gp_subtype_id
      if (this.isProbleChecklist && selectedGPSubtypes && selectedGPSubtypes.length) {
        // Отправляем первый подтип для каждого вопроса
        // Если нужно связать вопрос со всеми подтипами, здесь нужна дополнительная логика
        result.gp_subtype_id = selectedGPSubtypes[0];
      }

      return result;
    });

    // Для global_problem нам нужно найти чеклист выбранного подтипа
    let ticketTypeId = this.selectedChecklist.ticket_type;
    if (this.isProbleChecklist && selectedGPSubtypes && selectedGPSubtypes.length) {
      // Ищем чеклист для подтипа в списке всех чеклистов
      const subtypeId = selectedGPSubtypes[0];
      const subtypeChecklist = this.filterProblem(this.checklists$.getValue()).find((checklist) => checklist.gp_subtypes && checklist.gp_subtypes.includes(subtypeId));

      if (subtypeChecklist) {
        // Используем ticket_type из найденного чеклиста подтипа
        ticketTypeId = subtypeChecklist.ticket_type;
      }
    }

    return this.checkListsService.saveChecklistQuestions(ticketTypeId, saveChecklist);
  }

  private mapToGroups(questions: IQuestion[]): UntypedFormGroup[] {
    const groups = [];
    questions.forEach((question) => groups.push(this.fb.group({ selected: false, question })));
    return groups;
  }

  private tryToDeleteQuestions(groups: UntypedFormGroup[], params: ICheckListsFilters): void {
    this.checkListsService
      .tryToDeleteQuestions(groups.map((g) => g.value.question.id))
      .pipe(switchMap((errors: IDeleteQuestionError[]) => this.checkListsService.showDeleteDialogSuccess(errors)))
      .subscribe(() => this.handleApply(params));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
